import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronUp, Brain, MessageSquare, User, Bot, Sparkles, RotateCcw, TrendingUp, Hash } from 'lucide-react';
import { fetchMessage } from '../../services/api/chatHistory';
import { generateAISummary } from '../../services/api/commonData';

interface ChatThread {
  id: string;
  user_name: string;
  title: string;
  last_updated: string;
  messages: ChatMessage[];
  isExpanded: boolean;
}

interface ChatMessage {
  id: string;
  chat_id: string;
  content: string;
  sender: string;
  app_id: string;
}

interface AISummaryProps {
  chatThreads: ChatThread[];
  isLoading: boolean;
  assistant: any;
}

interface MessageSummary {
  id: string;
  threadTitle: string;
  userName: string;
  date: string;
  content: string;
  sender: string;
  threadId: string;
}

const AISummary: React.FC<AISummaryProps> = ({ chatThreads, isLoading, assistant }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  const [messageSummaries, setMessageSummaries] = useState<MessageSummary[]>([]);
  const [isFetchingMessages, setIsFetchingMessages] = useState(false);
  const [allThreadMessages, setAllThreadMessages] = useState<Record<string, ChatMessage[]>>({});

  // AI Summary states
  const [aiGeneratedSummary, setAiGeneratedSummary] = useState<string>('');
  const [mostDiscussedTopics, setMostDiscussedTopics] = useState<Array<{topic: string, messageCount: number}>>([]);
  const [isGeneratingAISummary, setIsGeneratingAISummary] = useState(false);
  const [showAISummary, setShowAISummary] = useState(false);
  const [isSectionExpanded, setIsSectionExpanded] = useState(false);

  // Create stable reference for thread IDs to prevent unnecessary re-fetches
  const threadIds = useMemo(() =>
    chatThreads.map(t => t.id).join(','),
    [chatThreads.map(t => t.id).join(',')]
  );

  // Fetch all messages for all threads automatically
  useEffect(() => {
    const fetchAllMessages = async () => {
      if (!chatThreads.length || !assistant?.app_id || isFetchingMessages) return;

      // Reset AI summary when new threads are loaded
      setAiGeneratedSummary('');
      setShowAISummary(false);

      setIsFetchingMessages(true);
      try {
        const messagePromises = chatThreads.map(async (thread) => {
          try {
            const messages = await fetchMessage(assistant.app_id, thread.id);
            return { threadId: thread.id, messages: messages || [] };
          } catch (error) {
            console.error(`Error fetching messages for thread ${thread.id}:`, error);
            return { threadId: thread.id, messages: [] };
          }
        });

        const results = await Promise.all(messagePromises);

        // Create a map of thread ID to messages
        const messagesMap: Record<string, ChatMessage[]> = {};
        results.forEach(({ threadId, messages }) => {
          messagesMap[threadId] = messages;
        });

        setAllThreadMessages(messagesMap);
      } catch (error) {
        console.error("Error fetching all messages:", error);
      } finally {
        setIsFetchingMessages(false);
      }
    };

    fetchAllMessages();
  }, [threadIds, assistant?.app_id]);

  // Generate message summaries from all fetched messages
  useEffect(() => {
    if (chatThreads.length > 0) {
      const allMessages: MessageSummary[] = [];

      chatThreads.forEach((thread) => {
        const threadMessages = allThreadMessages[thread.id] || [];

        if (threadMessages.length > 0) {
          threadMessages.forEach((message) => {
            allMessages.push({
              id: message.id,
              threadTitle: thread.title,
              userName: thread.user_name || 'Anonymous',
              date: new Date(thread.last_updated).toLocaleDateString(),
              content: message.content,
              sender: message.sender,
              threadId: thread.id,
            });
          });
        } else if (!isFetchingMessages) {
          // Only show placeholder if we're not currently fetching
          allMessages.push({
            id: `thread-${thread.id}`,
            threadTitle: thread.title,
            userName: thread.user_name || 'Anonymous',
            date: new Date(thread.last_updated).toLocaleDateString(),
            content: `Discussion: "${thread.title}" (Loading messages...)`,
            sender: 'thread',
            threadId: thread.id,
          });
        }
      });

      setMessageSummaries(allMessages);
    } else {
      setMessageSummaries([]);
    }
  }, [chatThreads, allThreadMessages, isFetchingMessages]);

  // Generate AI Summary from all messages
  const generateChatSummary = async () => {
    if (!chatThreads.length || !assistant?.app_id || Object.keys(allThreadMessages).length === 0) {
      return;
    }

    setIsGeneratingAISummary(true);
    try {
      // Format all messages as array of strings for discussion_points
      const discussionPoints: string[] = [];

      chatThreads.forEach((thread) => {
        const threadMessages = allThreadMessages[thread.id] || [];
        threadMessages.forEach((message) => {
          // Format each message as a readable string
          const formattedMessage = `Thread: "${thread.title}" | ${message.sender === 'user' ? 'User' : 'Assistant'}: ${message.content}`;
          discussionPoints.push(formattedMessage);
        });
      });

      const netId = localStorage.getItem("netId") || "anonymous";
      const userName = localStorage.getItem("userName") || "Anonymous User";

      const summaryRequest = {
        net_id: netId,
        app_id: assistant.app_id,
        discussion_points: discussionPoints,
        user_name: userName,
        chat_id: `summary_${Date.now()}`
      };

      // Call the AI summary service
      const result = await generateAISummary(summaryRequest);
      const cleanedSummary = result.summary ? cleanMarkdownFormatting(result.summary) : 'No summary generated';
      setAiGeneratedSummary(cleanedSummary);

      // Extract most discussed topics from result if available
      if (result.most_discussed_topics && Array.isArray(result.most_discussed_topics)) {
        setMostDiscussedTopics(result.most_discussed_topics);
      } else {
        // Fallback: generate topics from thread titles and message counts
        const topicCounts = chatThreads.map(thread => ({
          topic: thread.title,
          messageCount: allThreadMessages[thread.id]?.length || 0
        })).sort((a, b) => b.messageCount - a.messageCount);
        setMostDiscussedTopics(topicCounts);
      }

      setShowAISummary(true);
    } catch (error) {
      console.error("Error generating AI summary:", error);
      setAiGeneratedSummary('Failed to generate AI summary. Please try again.');

      // Fallback: generate topics from thread titles and message counts even on error
      const topicCounts = chatThreads.map(thread => ({
        topic: thread.title,
        messageCount: allThreadMessages[thread.id]?.length || 0
      })).sort((a, b) => b.messageCount - a.messageCount);
      setMostDiscussedTopics(topicCounts);

      setShowAISummary(true);
    } finally {
      setIsGeneratingAISummary(false);
    }
  };

  // Function to clean markdown formatting from AI summary
  const cleanMarkdownFormatting = (text: string): string => {
    return text
      // Convert markdown headers to HTML bold tags for main headings
      .replace(/^#{1,6}\s+(.+)$/gm, '\n\n<strong>$1:</strong>\n')
      // Convert bold markdown to HTML strong tags (**text** or __text__)
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      // Remove italic markdown (*text* or _text_)
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/_(.*?)_/g, '$1')
      // Remove code blocks (```text```)
      .replace(/```[\s\S]*?```/g, '')
      // Remove inline code (`text`)
      .replace(/`([^`]+)`/g, '$1')
      // Convert bullet points to clean format with proper spacing
      .replace(/^[\s]*[-\*]\s+(.+)$/gm, '• $1')
      // Convert numbered lists with proper spacing (keep numbers with parentheses)
      .replace(/^[\s]*(\d+)\.\s+(.+)$/gm, '$1) $2')
      // Add spacing between different sections
      .replace(/(\w)\n(•|\d+\))/g, '$1\n\n$2')
      // Add spacing after bullet/numbered lists before new sections
      .replace(/(•.*)\n([A-Z])/g, '$1\n\n$2')
      .replace(/(\d+\).*)\n([A-Z])/g, '$1\n\n$2')
      // Clean up excessive whitespace but preserve structure
      .replace(/\n\s*\n\s*\n+/g, '\n\n')
      // Ensure proper spacing after headings
      .replace(/:\n([^\n•\d])/g, ':\n\n$1')
      // Clean up any trailing/leading whitespace
      .replace(/^\s+|\s+$/g, '')
      .trim();
  };

  // Auto-generate AI summary when all messages are fetched
  useEffect(() => {
    if (!isFetchingMessages && Object.keys(allThreadMessages).length > 0 && chatThreads.length > 0 && !aiGeneratedSummary) {
      generateChatSummary();
    }
  }, [isFetchingMessages, allThreadMessages, threadIds]);

  // Don't render anything while loading
  if (isLoading) {
    return null;
  }

  // No data state
  if (chatThreads.length === 0) {
    return (
      <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-center space-x-3 text-gray-500">
          <MessageSquare className="h-8 w-8" />
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-600 mb-2">No Chat Data Available</h3>
            <p className="text-sm text-gray-500">
              No chat conversations match your current filters. Try adjusting your search criteria or date range to see results.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Helper functions
  const toggleThreadExpansion = (threadId: string) => {
    setExpandedThreads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(threadId)) {
        newSet.delete(threadId);
      } else {
        newSet.add(threadId);
      }
      return newSet;
    });
  };

  const getMessageIcon = (sender: string) => {
    switch (sender) {
      case 'user':
        return <User className="h-4 w-4 text-blue-600" />;
      case 'assistant':
        return <Bot className="h-4 w-4 text-green-600" />;
      default:
        return <MessageSquare className="h-4 w-4 text-gray-600" />;
    }
  };

  const getMessageLabel = (sender: string) => {
    switch (sender) {
      case 'user':
        return 'User asked:';
      case 'assistant':
        return 'AI responded:';
      default:
        return 'Thread:';
    }
  };

  const truncateContent = (content: string, maxLength: number = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  // Group messages by thread
  const messagesByThread = messageSummaries.reduce((acc, message) => {
    if (!acc[message.threadId]) {
      acc[message.threadId] = [];
    }
    acc[message.threadId].push(message);
    return acc;
  }, {} as Record<string, MessageSummary[]>);

  const totalMessages = messageSummaries.length;
  const totalThreads = Object.keys(messagesByThread).length;

  return (
    <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-blue-100/50 transition-colors duration-200"
        onClick={() => setIsSectionExpanded(!isSectionExpanded)}
      >
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            {/* <Brain className="h-5 w-5 text-blue-600" />  */}
            <Sparkles className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">AI Summary</h3>
            <p className="text-sm text-gray-600">
              {isFetchingMessages ? (
                <span className="flex items-center space-x-2">
                  <span className="animate-spin border-t-2 border-blue-600 rounded-full w-4 h-4"></span>
                  <span>Loading all messages...</span>
                </span>
              ) : isGeneratingAISummary ? (
                <span className="flex items-center space-x-2">
                  <Sparkles className="h-4 w-4 text-purple-600 animate-pulse" />
                  <span>Generating AI summary...</span>
                </span>
              ) : (
                `${totalMessages} message${totalMessages !== 1 ? 's' : ''} across ${totalThreads} conversation${totalThreads !== 1 ? 's' : ''}`
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {!isFetchingMessages && !isGeneratingAISummary && isSectionExpanded && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setAiGeneratedSummary('');
                setShowAISummary(false);
                generateChatSummary();
              }}
              className="flex items-center space-x-1 px-3 py-1 text-sm text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded-md transition-colors duration-200"
            >
              {aiGeneratedSummary ? (
                <RotateCcw className="h-4 w-4" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              <span>{aiGeneratedSummary ? 'Regenerate' : 'Generate'}</span>
            </button>
          )}
          {isSectionExpanded ? (
            <ChevronUp className="h-5 w-5 text-blue-600" />
          ) : (
            <ChevronDown className="h-5 w-5 text-blue-600" />
          )}
        </div>
      </div>

      {/* Collapsible Content */}
      {isSectionExpanded && (
        <>
          {/* AI Generated Summary Section */}
          {showAISummary && (
            <div className="border-t border-blue-200 bg-gradient-to-r from-purple-50 to-blue-50">
              <div className="p-4">
                <div className="flex gap-4">
                  {/* AI Summary - 3/4 width */}
                  <div className="flex-1" style={{flex: '3'}}>
                    <div className="bg-white rounded-lg p-4 border border-purple-200 shadow-sm h-full">
                      <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-100">
                        <div
                          className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap"
                          dangerouslySetInnerHTML={{ __html: aiGeneratedSummary }}
                          style={{
                            fontWeight: '400'
                          }}
                        />
                        <p className="text-xs text-gray-500 italic mt-4 pt-3 border-t border-purple-200">
                          Note: This summary is AI-generated and may contain inaccuracies or incomplete information.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Most Discussed Topics - 1/4 width */}
                  <div className="flex-1" style={{flex: '1'}}>
                    <div className="bg-white rounded-lg p-4 border border-blue-200 shadow-sm h-full">
                      <div className="flex items-center space-x-2 mb-3">
                        <TrendingUp className="h-4 w-4 text-orange-600" />
                        <h4 className="font-semibold text-gray-800">Top Topics</h4>
                      </div>
                      <div className="space-y-2">
                        {mostDiscussedTopics.length > 0 ? (
                          mostDiscussedTopics.slice(0, 8).map((topic, index) => (
                            <div key={index} className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-3 border border-gray-100 hover:shadow-md transition-all duration-200 group relative">
                              <div className="flex items-start justify-between space-x-2">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-800 leading-tight mb-1 truncate cursor-help" title={topic.topic}>
                                    {topic.topic.length > 45 ? `${topic.topic.substring(0, 45)}...` : topic.topic}
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <span className="inline-flex items-center bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs font-medium">
                                      {topic.messageCount} msg{topic.messageCount !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                </div>
                                {/* Orange dot indicator - commented out to give more space for topic text */}
                                {/* <div className="flex-shrink-0">
                                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-orange-400 to-red-400"></div>
                                </div> */}
                              </div>

                              {/* Tooltip for full topic name */}
                              {topic.topic.length > 45 && (
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 max-w-md break-words whitespace-normal">
                                  {topic.topic}
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="text-sm text-gray-500 text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                            <TrendingUp className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                            <p>No topics available</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

    </div>
  );
};

export default AISummary;